/**
 * Web Store Redirect Utilities
 *
 * Provides utilities for redirecting from legacy Web Store URLs (/store/:orgId/*)
 * to new standalone Web Store URLs (subdomain.tempustools.com/*).
 *
 * @module webStoreRedirect
 */

import { redirect } from 'react-router-dom'

import { API_BASE_URL } from '@/common/config/api'

/**
 * Transforms legacy Web Store path to new standalone Web Store path.
 *
 * Path transformation rules:
 * - `/store/:orgId/` → `/` (home)
 * - `/store/:orgId/login` → `/login` (preserved)
 * - `/store/:orgId/:quoteId` → `/orders/:quoteId` (quote → order)
 * - `/store/:orgId/:quoteId/checkout` → `/orders/:quoteId` (sub-paths stripped)
 * - `/store/:orgId/:quoteId/order-confirmation` → `/orders/:quoteId` (sub-paths stripped)
 *
 * The new standalone Web Store handles routing internally (e.g., review, checkout),
 * so we only preserve the order ID and let the new app determine the correct view.
 *
 * @param {string} pathname - Full pathname from legacy URL (e.g., "/store/123/quote-456/checkout")
 * @param {string} organisationId - Organization ID to remove from path
 * @returns {string} Transformed path for standalone Web Store (e.g., "/orders/quote-456")
 *
 * @example
 * transformWebStorePath('/store/123/', '123') // returns '/'
 * transformWebStorePath('/store/123/login', '123') // returns '/login'
 * transformWebStorePath('/store/123/quote-456', '123') // returns '/orders/quote-456'
 * transformWebStorePath('/store/123/quote-456/checkout', '123') // returns '/orders/quote-456'
 */
export function transformWebStorePath(pathname, organisationId) {
    // Remove the /store/:orgId prefix
    const prefix = `/store/${organisationId}`

    // Handle case where pathname doesn't start with expected prefix
    if (!pathname.startsWith(prefix)) {
        return '/'
    }

    const remainingPath = pathname.slice(prefix.length)

    // If empty or just /, return home
    if (!remainingPath || remainingPath === '/') {
        return '/'
    }

    // If /login, preserve it
    if (remainingPath === '/login') {
        return '/login'
    }

    // For any other path starting with /:id, transform to /orders/:id
    // This matches patterns like:
    // - /:quoteId → /orders/:quoteId
    // - /:quoteId/checkout → /orders/:quoteId
    // - /:quoteId/order-confirmation → /orders/:quoteId
    const match = remainingPath.match(/^\/([^/]+)/)
    if (match) {
        const id = match[1]
        return `/orders/${id}`
    }

    // Fallback to home for any unrecognized patterns
    return '/'
}

/**
 * Builds complete standalone Web Store URL with subdomain.
 *
 * Constructs URL in format: `protocol://subdomain.domain/path?query#hash`
 *
 * @param {string} protocol - URL protocol (e.g., "http:", "https:")
 * @param {string} subdomain - Organization's subdomain (e.g., "acme")
 * @param {string} path - Transformed path (e.g., "/orders/123")
 * @param {string} search - Query string including leading "?" (e.g., "?promo=ABC")
 * @param {string} hash - Hash fragment including leading "#" (e.g., "#section")
 * @param {string} [domain='tempustools.com'] - Base domain for standalone Web Store
 * @returns {string} Complete standalone Web Store URL
 *
 * @example
 * buildStandaloneUrl('https:', 'acme', '/orders/123', '?promo=ABC', '#top', 'tempustools.com')
 * // returns 'https://acme.tempustools.com/orders/123?promo=ABC#top'
 *
 * @example
 * buildStandaloneUrl('https:', 'acme', '/', '', '', 'tempustoolsinc.com')
 * // returns 'https://acme.tempustoolsinc.com/'
 */
export function buildStandaloneUrl(protocol, subdomain, path, search, hash, domain = 'tempustools.com') {
    return `${protocol}//${subdomain}.${domain}${path}${search}${hash}`
}

/**
 * React Router loader function for Web Store redirect.
 *
 * Intercepts requests to legacy Web Store URLs (/store/:organisationId/*)
 * and redirects to new standalone Web Store (subdomain.tempustools.com/*).
 *
 * This loader runs before the Web Store component renders, fetching the
 * organization's store settings to determine the subdomain and validate
 * that the store is enabled.
 *
 * @param {Object} args - React Router loader arguments
 * @param {Object} args.params - Route parameters containing organisationId
 * @param {Request} args.request - Request object containing URL info
 * @returns {Promise<Response>} Redirect response to standalone Web Store or fallback
 *
 * @example
 * // In router configuration:
 * {
 *   path: '/store/:organisationId/*',
 *   loader: webStoreRedirectLoader,
 *   element: <WebStore />
 * }
 *
 * @example
 * // User visits: https://app.quotemate.com/store/123/quote-456?promo=ABC
 * // Redirects to: https://acme.tempustools.com/orders/quote-456?promo=ABC
 */
export async function webStoreRedirectLoader(args) {
    const { params, request } = args
    const organisationId = params.organisationId
    const url = new URL(request.url)

    try {
        // Fetch store settings to get subdomain
        const apiUrl = `${API_BASE_URL}/organisations/${organisationId}/store`

        const response = await fetch(apiUrl)

        // If API call fails, redirect to fallback
        if (!response.ok) {
            return redirect('https://tempustools.com/')
        }

        const storeSettings = await response.json()

        // Validate store settings
        if (!storeSettings.webStoreIsEnabled || !storeSettings.subDomain) {
            return redirect('https://tempustools.com/')
        }

        // Transform path from legacy to new format
        const newPath = transformWebStorePath(url.pathname, organisationId)

        // Build complete standalone URL
        const domain = import.meta.env.VITE_WEBSTORE_STANDALONE_DOMAIN || 'tempustools.com'
        const newUrl = buildStandaloneUrl(url.protocol, storeSettings.subDomain, newPath, url.search, url.hash, domain)

        // Redirect to standalone Web Store
        return redirect(newUrl)
    } catch {
        // On any error, redirect to fallback
        return redirect('https://tempustools.com/')
    }
}
